import { Fragment, useState } from "react";
import { toast } from "sonner";
import { CaretDown, CaretRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import useAdminLoad from "@/hooks/useAdminLoad";
import { Empty, LoadState, PageHeader, Panel, Pill } from "@/components/admin/AdminUI";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";

const PAGE = 50;
const EMPTY = { q: "", topic: "all", status: "all", is_test: "all" };
const TOPIC = { general: "General", problem: "Problem", press: "Press" };

function Pick({ id, label, value, onChange, options }) {
  return (
    <div><Label htmlFor={id} className="text-xs">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id} className="mt-1 bg-white" data-testid={`${id}-select`}><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="all">All</SelectItem>{options.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`${id}-option-${k}`}>{l}</SelectItem>)}</SelectContent>
      </Select></div>
  );
}

function Row({ m, onStatus }) {
  const [open, setOpen] = useState(false);
  return (
    <Fragment>
      <tr className="cursor-pointer border-b border-border hover:bg-[var(--cw-surface)]" onClick={() => setOpen((o) => !o)} data-testid={`inbox-row-${m.id}`}>
        <td className="py-3 pl-4"><button aria-expanded={open} aria-label={open ? "Collapse" : "Expand"} className="flex h-8 w-8 items-center justify-center rounded-lg" data-testid={`inbox-expand-${m.id}`}>{open ? <CaretDown size={14} /> : <CaretRight size={14} />}</button></td>
        <td className="cw-soft whitespace-nowrap py-3 pr-4 text-sm">{formatDate(m.created_at)}</td>
        <td className="max-w-[220px] py-3 pr-4 text-sm"><span className="block truncate font-medium">{m.name || m.email}</span>{m.name && <span className="cw-muted block truncate text-xs">{m.email}</span>}</td>
        <td className="py-3 pr-4"><Pill tone={m.topic === "problem" ? "warning" : "neutral"}>{TOPIC[m.topic] || m.topic}</Pill>{m.is_test && <Pill className="ml-1" data-testid={`inbox-test-${m.id}`}>Test</Pill>}</td>
        <td className="cw-soft max-w-[320px] truncate py-3 pr-4 text-sm">{m.message}</td>
        <td className="py-3 pr-4"><Pill tone={m.status === "new" ? "brand" : "success"} className="capitalize" data-testid={`inbox-status-${m.id}`}>{m.status}</Pill></td>
      </tr>
      {open && (
        <tr className="border-b border-border bg-[var(--cw-surface)]" data-testid={`inbox-detail-${m.id}`}>
          <td colSpan={6} className="px-6 py-5">
            <p className="whitespace-pre-wrap text-sm leading-6">{m.message}</p>
            <p className="cw-muted mt-3 text-xs">{m.emailed ? "Emailed to the team" : "Logged, not emailed"}{m.handled_by ? ` · handled by ${m.handled_by}, ${formatDate(m.handled_at)}` : ""}</p>
            <div className="mt-4 flex gap-2">
              <Button size="sm" asChild variant="outline"><a href={`mailto:${m.email}?subject=${encodeURIComponent("Re: your message to Crushwink")}`} data-testid={`inbox-reply-${m.id}`}>Reply by email</a></Button>
              <Button size="sm" onClick={() => onStatus(m, m.status === "new" ? "handled" : "new")} data-testid={`inbox-toggle-${m.id}`}>{m.status === "new" ? "Mark handled" : "Mark as new"}</Button>
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

export default function SupportInbox() {
  const [form, setForm] = useState(EMPTY);
  const [filters, setFilters] = useState({ ...EMPTY, skip: 0 });
  const qs = new URLSearchParams({ limit: String(PAGE), skip: String(filters.skip) });
  ["q", "topic", "status", "is_test"].forEach((k) => filters[k] && filters[k] !== "all" && qs.set(k, filters[k]));
  const { data, error, loading, reload, setData } = useAdminLoad(`/admin/contact-messages?${qs}`);
  const pick = (k) => (v) => { setForm((f) => ({ ...f, [k]: v })); setFilters((f) => ({ ...f, [k]: v, skip: 0 })); };
  const onStatus = async (m, status) => {
    try {
      const r = await api(`/admin/contact-messages/${m.id}`, { method: "PATCH", body: { status } });
      setData((d) => ({ ...d, items: d.items.map((x) => (x.id === m.id ? { ...x, ...r.message } : x)), new_count: Math.max(0, (d.new_count || 0) + (m.is_test ? 0 : status === "handled" ? -1 : 1)) }));
      toast.success(status === "handled" ? "Marked handled." : "Marked as new.");
    } catch (e) { toast.error(e.message); }
  };
  const total = data?.total ?? 0;

  return (
    <div data-testid="admin-inbox-page">
      <PageHeader title="Support inbox" description="Messages from the website contact form. Reply by email, then mark them handled." />
      <form onSubmit={(e) => { e.preventDefault(); setFilters((f) => ({ ...f, q: form.q.trim(), skip: 0 })); }} className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr_1fr_auto] lg:items-end" data-testid="inbox-filters">
        <div><Label htmlFor="inbox-q" className="text-xs">Search name, email or message</Label><Input id="inbox-q" value={form.q} onChange={(e) => setForm((f) => ({ ...f, q: e.target.value }))} className="mt-1 bg-white" data-testid="inbox-q-input" /></div>
        <Pick id="inbox-topic" label="Topic" value={form.topic} onChange={pick("topic")} options={Object.entries(TOPIC)} />
        <Pick id="inbox-status" label="Status" value={form.status} onChange={pick("status")} options={[["new", "New"], ["handled", "Handled"]]} />
        <Pick id="inbox-test" label="Senders" value={form.is_test} onChange={pick("is_test")} options={[["false", "Real"], ["true", "Test"]]} />
        <Button type="submit" data-testid="inbox-search-button">Search</Button>
      </form>
      <LoadState loading={loading && !data} error={error} onRetry={reload}>
        <Panel className="overflow-hidden">
          <p className="cw-muted border-b border-border px-5 py-3 text-xs" data-testid="inbox-total">{total} message{total === 1 ? "" : "s"} · <span className="cw-brand-deep font-medium" data-testid="inbox-new-count">{data?.new_count ?? 0} new</span> (real senders)</p>
          {data?.items?.length ? (
            <div className="overflow-x-auto"><table className="w-full min-w-[860px] text-left" data-testid="inbox-table">
              <thead><tr className="cw-muted border-b border-border text-xs"><th className="w-12 py-3 pl-4" /><th className="py-3 pr-4 font-medium">When</th><th className="py-3 pr-4 font-medium">From</th><th className="py-3 pr-4 font-medium">Topic</th><th className="py-3 pr-4 font-medium">Message</th><th className="py-3 pr-4 font-medium">Status</th></tr></thead>
              <tbody>{data.items.map((m) => <Row key={m.id} m={m} onStatus={onStatus} />)}</tbody>
            </table></div>
          ) : <Empty title="No messages match" testId="inbox-empty" />}
          {total > PAGE && (
            <div className="flex justify-end gap-2 border-t border-border px-5 py-3">
              <Button size="sm" variant="outline" disabled={filters.skip === 0} onClick={() => setFilters((f) => ({ ...f, skip: f.skip - PAGE }))} data-testid="inbox-prev-button">Previous</Button>
              <Button size="sm" variant="outline" disabled={filters.skip + PAGE >= total} onClick={() => setFilters((f) => ({ ...f, skip: f.skip + PAGE }))} data-testid="inbox-next-button">Next</Button>
            </div>
          )}
        </Panel>
      </LoadState>
    </div>
  );
}
